import { useEffect, useRef } from 'react';
import { clearApiCache } from '../services/api';

// Single global BroadcastChannel for 0ms tab-to-tab real-time sync across all portals
const channel = typeof window !== 'undefined' && 'BroadcastChannel' in window 
  ? new BroadcastChannel('musb_realtime_sync') 
  : null;

/**
 * Global helper function to trigger real-time mutation events across all browser portals, tabs, and windows.
 * 
 * @param {string} eventType - E.g. 'OFFER_MUTATED', 'BOOKING_MUTATED', 'PHLEB_MUTATED', 'LAB_MUTATED', 'PAYOUT_MUTATED'
 * @param {object} payload - Relevant event payload
 */
export function emitRealtimeMutation(eventType = 'REALTIME_MUTATED', payload = {}) {
  try {
    clearApiCache();
    const data = { event: eventType, payload, timestamp: Date.now() };
    
    // 1. Native BroadcastChannel (0ms sync across tabs/portals)
    if (channel) {
      channel.postMessage(data);
    }
    
    // 2. Trigger localStorage storage event for cross-window sync
    localStorage.setItem('musb_last_mutation', JSON.stringify(data));
  } catch (err) {
    console.error('Error emitting real-time mutation:', err);
  }
}

// Module-level singleton EventSource to prevent connection storms and WSGI thread exhaustion
let globalEventSource = null;
let globalReconnectTimeout = null;
let globalAttempt = 0;
const globalSubscribers = new Set();

function initGlobalEventSource() {
  if (typeof window === 'undefined') return;
  if (globalEventSource) return;
  if (globalSubscribers.size === 0) return;

  try {
    const backendBase = process.env.REACT_APP_API_BASE_URL
      ? (process.env.REACT_APP_API_BASE_URL.endsWith('/') ? process.env.REACT_APP_API_BASE_URL : `${process.env.REACT_APP_API_BASE_URL}/`)
      : '';
    const sseUrl = backendBase ? `${backendBase}api/offers/stream/` : '/api/offers/stream/';

    globalEventSource = new EventSource(sseUrl);

    globalEventSource.onopen = () => {
      globalAttempt = 0;
    };

    globalEventSource.onmessage = (e) => {
      try {
        if (!e.data) return;
        const data = JSON.parse(e.data);
        clearApiCache();
        globalSubscribers.forEach((sub) => {
          try {
            sub(data);
          } catch (err) {
            console.error('SSE subscriber callback error:', err);
          }
        });
      } catch {
        // Ignore non-json keepalives (e.g. : ping)
      }
    };

    globalEventSource.onerror = () => {
      if (globalEventSource) {
        globalEventSource.close();
        globalEventSource = null;
      }
      if (globalSubscribers.size === 0) return;

      // Exponential backoff capped at 30s
      const delay = Math.min(2000 * Math.pow(2, globalAttempt), 30000);
      globalAttempt += 1;
      if (globalReconnectTimeout) clearTimeout(globalReconnectTimeout);
      globalReconnectTimeout = setTimeout(() => {
        if (globalSubscribers.size > 0 && !globalEventSource) {
          initGlobalEventSource();
        }
      }, delay);
    };
  } catch (err) {
    // Handled gracefully by BroadcastChannel fallback
  }
}

function teardownGlobalEventSource() {
  if (globalSubscribers.size === 0) {
    if (globalReconnectTimeout) {
      clearTimeout(globalReconnectTimeout);
      globalReconnectTimeout = null;
    }
    if (globalEventSource) {
      globalEventSource.close();
      globalEventSource = null;
    }
  }
}

/**
 * Universal React Hook for listening to real-time events across all portals (Super Admin, Hub, Lab, Phleb, Patient).
 *
 * @param {Function} onMutation - Callback function triggered when a real-time event arrives
 * @param {Array<string>} targetEvents - Optional array of event names to listen to (or empty/null for all)
 */
export function useRealtimeStream(onMutation, targetEvents = null, options = {}) {
  const { sse = true } = options;
  const onMutationRef = useRef(onMutation);
  onMutationRef.current = onMutation;

  const targetEventsRef = useRef(targetEvents);
  targetEventsRef.current = targetEvents;

  useEffect(() => {
    const handleMutation = (data) => {
      try {
        if (!data) return;
        clearApiCache();
        const evtName = data.event || data.action || 'REALTIME_MUTATED';
        const targets = targetEventsRef.current;
        if (!targets || targets.includes(evtName) || evtName === 'REALTIME_MUTATED' || evtName === 'OFFER_MUTATED') {
          if (typeof onMutationRef.current === 'function') {
            onMutationRef.current(data);
          }
        }
      } catch (err) {
        console.error('Error handling real-time stream callback:', err);
      }
    };

    // Layer 1: BroadcastChannel Listener (0ms instant tab/portal sync)
    const handleChannelMessage = (e) => {
      if (e.data) {
        handleMutation(e.data);
      }
    };
    if (channel) {
      channel.addEventListener('message', handleChannelMessage);
    }

    // Layer 2: Storage Event Listener (Cross-window sync)
    const handleStorageChange = (e) => {
      if (e.key === 'musb_last_mutation' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          handleMutation(parsed);
        } catch {
          // ignore malformed storage values
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Layer 3: Shared Singleton SSE connection (at most 1 connection per browser tab)
    if (sse) {
      globalSubscribers.add(handleMutation);
      initGlobalEventSource();
    }

    return () => {
      if (channel) {
        channel.removeEventListener('message', handleChannelMessage);
      }
      window.removeEventListener('storage', handleStorageChange);
      if (sse) {
        globalSubscribers.delete(handleMutation);
        teardownGlobalEventSource();
      }
    };
  }, [sse]);
}

// Backwards compatibility alias for useOfferStream
export const useOfferStream = useRealtimeStream;

export default useRealtimeStream;
